import React, { useState, useCallback } from 'react';

/**
 * Hover/focus tooltip (no delay). Uses over/out (bubbling) so moving onto a child
 * <button> still hits the wrapper — enter/leave on the parent often never fire in React.
 * Use placement="below" for controls under overflow:hidden or flush to the viewport top.
 */
export function TooltipHost({ label, children, placement = 'above' }) {
  const [visible, setVisible] = useState(false);

  const show = useCallback(() => setVisible(true), []);
  const hide = useCallback(() => setVisible(false), []);

  const handleOut = useCallback((e) => {
    const next = e.relatedTarget;
    if (next instanceof Node && e.currentTarget.contains(next)) return;
    hide();
  }, [hide]);

  const handleBlurCapture = useCallback((e) => {
    const next = e.relatedTarget;
    if (next instanceof Node && e.currentTarget.contains(next)) return;
    hide();
  }, [hide]);

  if (!label) return children;

  const isBelow = placement === 'below';
  const tipStyle = isBelow
    ? {
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: 8,
      }
    : {
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: 8,
      };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
      }}
      onPointerOver={show}
      onPointerOut={handleOut}
      onMouseOver={show}
      onMouseOut={handleOut}
      onFocusCapture={show}
      onBlurCapture={handleBlurCapture}
    >
      {children}
      {visible && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            ...tipStyle,
            padding: '6px 10px',
            backgroundColor: '#0f172a',
            color: '#f8fafc',
            border: '1px solid #64748b',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            maxWidth: 'min(300px, 90vw)',
            width: 'max-content',
            zIndex: 2147483647,
            pointerEvents: 'none',
            boxShadow: '0 12px 40px rgba(0,0,0,0.75)',
            lineHeight: 1.45,
            textAlign: 'center',
            wordWrap: 'break-word',
          }}
        >
          {label}
        </span>
      )}
    </span>
  );
}
